import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for scroll-triggered animations using Intersection Observer
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Intersection threshold (0-1)
 * @param {string} options.rootMargin - Root margin for intersection
 * @param {boolean} options.triggerOnce - Whether to trigger animation only once
 * @param {number} options.delay - Animation delay in milliseconds
 * @returns {[React.RefObject, boolean]} - [ref, isVisible]
 */
export const useScrollAnimation = (options = {}) => {
  const {
    threshold = 0.15,
    rootMargin = '0px 0px -100px 0px',
    triggerOnce = false,
    delay = 0
  } = options;

  const [isVisible, setIsVisible] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (delay > 0) {
            setTimeout(() => {
              setIsVisible(true);
              if (triggerOnce) setHasTriggered(true);
            }, delay);
          } else {
            setIsVisible(true);
            if (triggerOnce) setHasTriggered(true);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, rootMargin, triggerOnce, delay]);

  // Reset visibility if not triggerOnce and hasn't triggered yet
  useEffect(() => {
    if (!triggerOnce && !hasTriggered) {
      setIsVisible(false);
    }
  }, [triggerOnce, hasTriggered]);

  return [ref, isVisible];
};

/**
 * Hook to detect scroll direction
 * @returns {string} - 'up' or 'down'
 */
export const useScrollDirection = () => {
  const [scrollDirection, setScrollDirection] = useState('up');
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setScrollDirection('down');
      } else {
        setScrollDirection('up');
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return scrollDirection;
};

/**
 * CSS classes for scroll-triggered animations
 */
export const scrollAnimationClasses = {
  fadeInUp: 'opacity-0 translate-y-6 scroll-animated',
  fadeInDown: 'opacity-0 -translate-y-6 scroll-animated',
  fadeInLeft: 'animate-slide-from-left',
  fadeInRight: 'animate-slide-from-right',
  scaleIn: 'opacity-0 scale-95 scroll-animated',
  slideInUp: 'opacity-0 translate-y-12 scroll-animated',
  slideInDown: 'opacity-0 -translate-y-12 scroll-animated',
  rotateIn: 'opacity-0 rotate-6 scroll-animated',
  
  // Visible states
  visible: 'opacity-100 translate-y-0 translate-x-0 scale-100 rotate-0 visible',
  
  // Stagger delays
  stagger1: 'delay-150',
  stagger2: 'delay-300',
  stagger3: 'delay-450',
  stagger4: 'delay-600',
  stagger5: 'delay-750',
  stagger6: 'delay-900'
};

/**
 * Helper function to create scroll animation classes
 * @param {string} animationType - Type of animation
 * @param {boolean} isVisible - Whether element is visible
 * @returns {string} - Combined CSS classes
 */
export const createScrollAnimationClasses = (animationType, isVisible) => {
  const baseClasses = scrollAnimationClasses[animationType] || scrollAnimationClasses.fadeInUp;
  const visibleClasses = isVisible ? scrollAnimationClasses.visible : '';
  
  return `${baseClasses} ${visibleClasses}`.trim();
};

/**
 * Higher-order component for scroll animations
 */
export const withScrollAnimation = (WrappedComponent, animationType = 'fadeInUp', options = {}) => {
  return function ScrollAnimatedComponent(props) {
    const [ref, isVisible] = useScrollAnimation(options);
    const animationClasses = createScrollAnimationClasses(animationType, isVisible);
    
    return (
      <div ref={ref} className={animationClasses}>
        <WrappedComponent {...props} />
      </div>
    );
  };
};

/**
 * Animation presets for common use cases
 */
export const animationPresets = {
  // Staggered animations for lists
  staggeredList: (items, baseDelay = 0) => 
    items.map((_, index) => ({
      delay: baseDelay + (index * 100),
      animation: 'fadeInUp'
    })),
  
  // Hero section animations
  heroSection: {
    badge: { delay: 0, animation: 'fadeInUp' },
    title: { delay: 200, animation: 'fadeInUp' },
    subtitle: { delay: 400, animation: 'fadeInUp' },
    stats: { delay: 600, animation: 'fadeInUp' },
    buttons: { delay: 800, animation: 'fadeInUp' },
    image: { delay: 1000, animation: 'fadeInRight' }
  },
  
  // Card grid animations
  cardGrid: (count, baseDelay = 0) => 
    Array.from({ length: count }, (_, index) => ({
      delay: baseDelay + (index * 150),
      animation: 'fadeInUp'
    })),
  
  // Side-by-side content animations
  sideBySide: {
    left: { delay: 0, animation: 'fadeInLeft' },
    right: { delay: 200, animation: 'fadeInRight' }
  }
};

/**
 * Utility functions for animation management
 */
export const animationUtils = {
  // Get animation delay based on index
  getStaggerDelay: (index, baseDelay = 0, staggerAmount = 100) => 
    baseDelay + (index * staggerAmount),
  
  // Create animation classes with custom timing
  createCustomAnimation: (animationType, isVisible, customDelay = 0) => {
    const baseClasses = scrollAnimationClasses[animationType] || scrollAnimationClasses.fadeInUp;
    const visibleClasses = isVisible ? scrollAnimationClasses.visible : '';
    const delayClass = customDelay > 0 ? `delay-[${customDelay}ms]` : '';
    
    return `${baseClasses} ${visibleClasses} ${delayClass}`.trim();
  },
  
  // Check if element is in viewport
  isInViewport: (element, threshold = 0.15) => {
    if (!element) return false;
    
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    
    return (
      rect.top <= windowHeight * (1 - threshold) &&
      rect.bottom >= windowHeight * threshold
    );
  }
};
